// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import clsx from 'clsx';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';
import {
    SpinnerSize,
    ActionButton,
    PrimaryButton,
    Icon,
    TextField,
    TooltipHost,
} from 'office-ui-fabric-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { logPageAction } from './../../lib/ApplicationInsights';
import history from 'src/history';
import BusySpinner from './../../components/BusySpinner';
import CommonLayout from './../../components/CommonLayout';
// import Toggler from './../../components/Toggler';
import { LoadingState, actionStatusSelector } from './../../store/actionStatus';
import {
    GET_ALL_USERS,
    GET_SOLD_TO_SHIP_TO,
    createUserStatusSelector,
    deleteUserStatusSelector,
    getAllUsers,
    getChannels,
    getRoles,
    getSoldToShipTo,
    // postUser,
    deleteUser,
    soldToShipToSelector,
    usersSelector,
    clearUserMessage,
} from './../../store/manageUsers';
import {
    addAadUserStatusSelector,
    clearAadUserMessage,
} from './../../store/manageAadOps';
import AddOrUpdate, { RetailUserType } from './AddOrUpdate';
import getColumnHeaders from './columnHeaders';
import UserInfoDal from 'src/dal/UserInfoDal';
import Styles from './manageUsers.module.scss';
import { userInfoSelector } from './../../store/user';
import Message from './../../components/Messages';
import { capitalize, uniqBy } from 'lodash';
import { filterData, FilterOptions } from '../../helpers/dataTableUtilities';

export const initialRetailUserState: RetailUserType = {
    channelId: '',
    channelName: '',
    createdEmailId: '',
    domainAlias: '',
    email: '',
    firstName: '',
    isActive: true,
    isWarrantyUpdateEnabled: false,
    lastName: '',
    retailUserId: 0,
    roleId: '',
    roleName: '',
    shiptoCustomerNbr: '',
    soldToCustomerNbr: '',
    tierName: '',
    aadUserId: '',
    inviteRedeemUrl: '',
};

interface ExportRetailUser {
    firstName?: string;
    lastName?: string;
    email?: string;
    roleName?: string;
    channelName?: string;
    tierName?: string | number;
    soldtoCustomerNbr?: string | number;
    soldtoCustomerName?: string;
    shiptoCustomerNbr?: string | number;
    shiptoCustomerName?: string;
    isWarrantyUpdateEnabled?: string;
    isActive?: string;
    aadUserStatus?: string;
    requestAccessStatus?: string;
}

export default function ManageUsers(props: any) {

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [addNewRecord, setAddNewRecord] = React.useState(false);
    const [editing, setEditing] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const [retailUser, setRetailUser] = React.useState<RetailUserType>(initialRetailUserState);
    const [searchText, setSearchText] = React.useState<string | undefined>('');

    const [usersList, setUsersList] = React.useState(Array(0));
    const [filteredUsersData, setFilteredUsersData] = React.useState(Array(0));
    const [rolesList, setRolesList] = React.useState(Array(0));
    const [tiersList, setTiersList] = React.useState(Array(0));
    const [selectedRole, setSelectedRole] = React.useState<any>(undefined);
    const [selectedTier, setSelectedTier] = React.useState<any>(undefined);
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [pageSet, setPageSet] = React.useState<number>(10);
    const [totalRecordCount, setTotalRecordCount] = React.useState<number>(0);
    const [totalPages, setTotalPages] = React.useState<number>(0);
    const [requestedUserIds, setRequestedUserIds] = React.useState<number[]>([]);
    const [submittingUserIds, setSubmittingUserIds] = React.useState<number[]>([]);
    const [requestAccessErrorIds, setRequestAccessErrorIds] = React.useState<number[]>([]);

    const usersStatus = useSelector(state => actionStatusSelector(state)(GET_ALL_USERS));
    const soldToShipToStatus = useSelector(state => actionStatusSelector(state)(GET_SOLD_TO_SHIP_TO));
    const userInfo = useSelector(state => userInfoSelector(state));
    const allUsers = useSelector(state => usersSelector(state));
    const soldToShipToData = useSelector(state => soldToShipToSelector(state));
    const users = allUsers.map((user: any) => ({ ...user, userStatus: user.isActive === true ? t('active') : t('inactive'), roleName: t(user.roleName) }));
    const userCreationResponse = useSelector(state => createUserStatusSelector(state));
    const userDeletionResponse = useSelector(state => deleteUserStatusSelector(state));
    const addAadUserResponse = useSelector(state => addAadUserStatusSelector(state));

    let renderPageNumbers;
    const exportFileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const exportFileName = `RetailUsersData.xlsx`;

    const exportExcelColumnHeader: any = {
        firstName: t('first_name'),
        lastName: t('last_name'),
        email: t('email'),
        roleName: t('role'),
        channelName: t('channel'),
        tierName: t('tier_name'),
        soldtoCustomerNbr: t('sold_to'),
        soldtoCustomerName: t('add_sold_to_partner_name'),
        shiptoCustomerNbr: t('ship_to'),
        shiptoCustomerName: t('add_ship_to_partner_name'),
        isWarrantyUpdateEnabled: t('Warranty Admin Status'),
        aadUserStatus: t('user_status'),
        requestAccessStatus: t('request_access_back'),
    };

    React.useEffect(() => {
        dispatch(getAllUsers());
        dispatch(getSoldToShipTo());
        dispatch(getRoles());
        dispatch(getChannels());
    }, []);

    React.useEffect(() => {
        //Updating the table after a record is added or edited
        if (editing && userCreationResponse && userCreationResponse.status === 'succeeded') {
            dispatch(getAllUsers());
        }
    }, [userCreationResponse]);

    React.useEffect(() => {
        //Updating the table after a record is added or edited
        if (!editing && addAadUserResponse && addAadUserResponse.status === 'succeeded') {
            dispatch(getAllUsers());
        }
    }, [addAadUserResponse]);

    React.useEffect(() => {
        //Updating the table after a record is deleted
        if (deleting && userDeletionResponse && userDeletionResponse.status === 'succeeded') {
            dispatch(getAllUsers());
        }
    }, [userDeletionResponse]);

    const actionButtons = (item: any) => {
        const onEditClick = (event: any) => {
            const args = JSON.parse(event.currentTarget.dataset.arg);
            handleEdit(args);
        };
        const onDeleteClick = (event: any) => {
            const args = JSON.parse(event.currentTarget.dataset.arg);
            handleDelete(args);
        };
        return (<div className={Styles.actionContainer}>
            <TooltipHost
                hostClassName={Styles.modalToolTip}
                aria-label={t('Edit User')}
                content={t('edit_user_data')}>
                <ActionButton
                    ariaLabel={t('edit')}
                    iconProps={{
                        ariaLabel: t('edit'),
                        iconName: 'Edit',
                    }}
                    onClick={onEditClick}
                    data-arg={JSON.stringify(item)}
                />
            </TooltipHost>
            {userInfo.email && userInfo.email !== '' && item.email && item.email !== '' && userInfo.email.trim().toLowerCase() !== item.email.trim().toLowerCase() ? <TooltipHost
                hostClassName={Styles.modalToolTip}
                aria-label={t('Delete User')}
                content={t('delete_user')}>
                <ActionButton
                    ariaLabel={t('delete')}
                    className={Styles.deleteIcon}
                    iconProps={{
                        ariaLabel: t('delete'),
                        iconName: 'Delete',
                    }}
                    onClick={onDeleteClick}
                    data-arg={JSON.stringify(item)}
                />
            </TooltipHost> : ''}
        </div>);
    };

    const accessStatusMessage = (item: any) => {
        const status = item.aadUserStatus;
        const isRequested = (item.retailUserId !== undefined && requestedUserIds.includes(item.retailUserId))
            || !!item.accessRequestSubmittedDate;
        const isSubmitting = item.retailUserId !== undefined && submittingUserIds.includes(item.retailUserId);
        const hasRequestError = item.retailUserId !== undefined && requestAccessErrorIds.includes(item.retailUserId);
        if (status === 'Disabled') {
            return (
                <div className={Styles.requestAccessContainer}>
                    {!isRequested && <span>{t('request_access_disabled_text')}</span>}
                    {hasRequestError && <span role="alert">{t('request_access_error')}</span>}
                    {isRequested
                        ? <span>
                            <span className={Styles.requestSubmitted}>{t('request_submitted')}</span>
                            {' '}{t('request_submitted_detail')}
                        </span>
                        : <PrimaryButton
                            className={Styles.raiseRequestButton}
                            text={isSubmitting ? t('submitting') : t('raise_request')}
                            ariaLabel={t('raise_request')}
                            onClick={() => handleRequestAccessBack(item)}
                            disabled={isSubmitting}
                        />
                    }
                </div>
            );
        }
        if (status === 'Not Found') {
            return <span>{t('request_access_notfound_text')}</span>;
        }
        return null;
    };

    async function handleRequestAccessBack(item: RetailUserType) {
        logPageAction('Request Access Back');
        if (item.retailUserId === undefined) {
            return;
        }
        const id = item.retailUserId as number;
        setRequestAccessErrorIds(prev => prev.filter(x => x !== id));
        setSubmittingUserIds(prev => prev.includes(id) ? prev : [...prev, id]);
        try {
            await UserInfoDal.requestAccessBack({ payload: { userEmail: item.email, retailUserId: item.retailUserId } });
            setRequestedUserIds(prev => prev.includes(id) ? prev : [...prev, id]);
        } catch (error) {
            setRequestAccessErrorIds(prev => prev.includes(id) ? prev : [...prev, id]);
        } finally {
            setSubmittingUserIds(prev => prev.filter(x => x !== id));
        }
    }

    React.useEffect(() => {
        setPageSet(10);
        fetchCurrentUsersList(pageNumber, '');
    }, [allUsers, searchText, selectedRole, selectedTier]);

    const onDropdownFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
        const el = e.currentTarget;
        el.style.borderColor = 'black';
        el.style.outlineStyle = 'auto';
        el.style.outlineOffset = '3px';
    };

    const onDropdownBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
        const el = e.currentTarget;
        el.style.borderColor = '';
        el.style.outlineStyle = 'none';
        el.style.outlineOffset = '';
    };

    const pageNumbersList = [];
    if (totalRecordCount !== null) {
        const totalPagesCount = (totalRecordCount % pageSet) > 0 ? (totalRecordCount / pageSet) + 1 : totalRecordCount / pageSet;
        for (let i = 1; i <= totalPagesCount; i++) {
            pageNumbersList.push(i);
        }
        renderPageNumbers = pageNumbersList.map(number => {
            const activeClass = pageNumber === number ? Styles.active : '';
            const visibleStyle = (number >= pageNumber - 2 && number <= pageNumber + 2) ? { display: '' } : (number >= pageNumber - 2 && number <= 5 ? { display: '' } : { display: 'none' });
            const labelName = `Page ${number}`;
            return (
                <div tabIndex={0}
                    role="button"
                    aria-label={labelName}
                    key={number}
                    style={visibleStyle}
                    className={clsx(activeClass)}
                    data-arg={number}
                    onClick={handleOnClick}>
                    {number}
                </div>
            );
        });
    }

    const isLoading = !editing && (usersStatus.status === LoadingState.started
        || usersStatus.status === LoadingState.notStarted
        || soldToShipToStatus.status === LoadingState.started
        || soldToShipToStatus.status === LoadingState.notStarted);

    function handleEdit(args: any) {
        dispatch(clearUserMessage());
        setEditing(true);
        setRetailUser({ ...args });
        setAddNewRecord(true);
    }

    function handleDelete(args: any) {
        dispatch(clearUserMessage());
        if (userInfo.email && userInfo.email !== '' && args.email && args.email !== '') {
            setDeleting(true);
            const deleteUserPayload = {
                requesterEmail: userInfo.email,
                userEmail: args.email,
            };
            dispatch(deleteUser(deleteUserPayload));
        }
    }

    const getFilteredResults = (searchText: any) => {
        const filterOptions: FilterOptions = {
            searchText: searchText.toString(),
            filters: {},
        };
        const filteredItems = filterData(users, filterOptions);
        return filteredItems;
    };

    function onClickAddNew(event: any) {
        dispatch(clearUserMessage());
        dispatch(clearAadUserMessage());
        setEditing(false);
        setRetailUser(initialRetailUserState);
        setAddNewRecord(true);
    }

    function onCloseOrOpen(show: boolean) {
        setAddNewRecord(show);
    }

    function getSoldToShipToObj(soldTo: any, shipTo: any) {
        let returnObj: any;
        try {
            returnObj = soldToShipToData.filter((x: any) => x.soldtoCustomerNbr === soldTo && x.shiptoCustomerNbr === shipTo)[0];
        }
        catch (e) {
            returnObj = null;
        }
        return returnObj;
    }

    function buildToBeExportedData(filteredUsersData: RetailUserType[]) {
        const exportDataList: ExportRetailUser[] = [];
        filteredUsersData.forEach((userData: RetailUserType) => {
            const isRequested = (userData.retailUserId !== undefined && requestedUserIds.includes(userData.retailUserId))
                || !!userData.accessRequestSubmittedDate;

            const exportData: ExportRetailUser = {
                firstName: userData.firstName,
                lastName: userData.lastName,
                email: userData.email,
                roleName: userData.roleName,
                channelName: userData.channelName,
                tierName: userData.tierName.toString().trim(),
                soldtoCustomerNbr: userData.soldToCustomerNbr,
                soldtoCustomerName: getSoldToShipToObj(userData.soldToCustomerNbr?.toString(), userData.shiptoCustomerNbr?.toString())?.soldtoCustomerName,
                shiptoCustomerNbr: userData.shiptoCustomerNbr,
                shiptoCustomerName: getSoldToShipToObj(userData.soldToCustomerNbr?.toString(), userData.shiptoCustomerNbr?.toString())?.shiptoCustomerName,
                isWarrantyUpdateEnabled: userData.isWarrantyUpdateEnabled ? 'Enabled' : 'Disabled',
                aadUserStatus: userData.aadUserStatus,
                requestAccessStatus: userData.aadUserStatus === 'Disabled'
                    ? (isRequested
                        ? `${t('request_submitted')} - ${t('request_submitted_detail')}`
                        : t('request_access_disabled_text'))
                    : userData.aadUserStatus === 'Not Found'
                        ? t('request_access_notfound_text')
                        : '',
            };
            exportDataList.push(exportData);
        });
        return exportDataList;
    }

    const exportBulkUsersClick = () => {
        if (filteredUsersData && filteredUsersData.length > 0) {
            const exportUsersData = buildToBeExportedData(filteredUsersData);
            exportUsersData.unshift(exportExcelColumnHeader);
            const ws = XLSX.utils.json_to_sheet(exportUsersData, { skipHeader: true });
            const wb = { Sheets: { 'Retail Users List': ws }, SheetNames: ['Retail Users List'] };
            const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const data = new Blob([excelBuffer], { type: exportFileType });
            FileSaver.saveAs(data, exportFileName);
        }
    };

    const uploadBulkUsersClick = () => {
        logPageAction('Open Upload Bulk Users');
        history.push('manageUsers/manageBulkUsers');
    };

    function addDropdownClass(event: any) {
        setTimeout(() => {
            //Filter Search Input 
            const titleDiv = document.getElementsByClassName('ms-SearchBox')[0];
            titleDiv ? titleDiv.setAttribute('id', 'searchBox') : null;
            titleDiv ? titleDiv.setAttribute('aria-label', 'Filter Options') : null;
            titleDiv ? titleDiv.classList.add('searchFocus') : null;
            titleDiv ? titleDiv.setAttribute('tabIndex', '0') : null;
            // Search, MenuList, Clear button
            const parentA = document.getElementsByClassName('ms-ContextualMenu-container')[0];
            parentA ? parentA.setAttribute('role', 'application') : null;
            parentA ? parentA.setAttribute('aria-label', 'Dropdown') : null;
            parentA ? parentA.setAttribute('tabIndex', '0') : null;
            //First menu item in the list 
            const divElem = document.querySelectorAll('.ms-ContextualMenu-link');
            divElem && divElem[0] ? divElem[0].setAttribute('id', 'menuBtn') : null;
            // Adding listener to outer searchBox
            const el = document.getElementById('searchBox');
            el ?
                el.addEventListener('keydown', keyDownEvent) : null;
        }, 0);
    }

    function keyDownEvent(e: { which: any; }) {
        if (e && e.which === 40) {
            const titleDiv = document.getElementsByClassName('ms-SearchBox')[0];
            titleDiv ? titleDiv.setAttribute('tabIndex', '2') : null;
            // Focusing the first menu item in the list when keydown is pressed from search filter input field
            const searchBox = document.getElementById('menuBtn');
            setTimeout((e) => {
                searchBox ? searchBox.focus() : null;
                const titleDiv = document.getElementsByClassName('ms-SearchBox')[0];
                // To navigate from clear button to search filter field when keydown is pressed
                titleDiv ? titleDiv.setAttribute('tabIndex', '0') : null;
            }, 0);
        }
    }

    React.useEffect(() => {
        return () => {
            const btnClass = document.getElementsByClassName('filter-button-dropdown');
            const arr = Array.from(btnClass);
            const el = document.getElementById('searchBox');
            el ?
                el.removeEventListener('keydown', keyDownEvent) : null;
            arr.map((e) => {
                e.removeEventListener('click', addDropdownClass);
            });
        };
    }, []);

    const toolbarButton = <div className="ms-Grid-col">
        <ActionButton
            className={Styles.addNewButton}
            iconProps={{ iconName: 'Save' }}
            ariaLabel={t('add_new_record')}
            text={t('add_new_record')}
            onClick={onClickAddNew} />
        <ActionButton
            className={Styles.addNewButton}
            iconProps={{ iconName: 'Upload' }}
            ariaLabel={t('manage_bulk_users')}
            text={t('manage_bulk_users')}
            onClick={uploadBulkUsersClick} />
        <ActionButton
            className={Styles.addNewButton}
            iconProps={{ iconName: 'ExcelDocument' }}
            ariaLabel={t('export_bulk_users')}
            text={t('export_bulk_users')}
            onClick={exportBulkUsersClick} />
    </div>;

    const successMessage = deleting
        && userDeletionResponse.status
        && userDeletionResponse.status.toLowerCase() === 'succeeded'
        ? t('user_deleted_successfully') : ((editing && !addNewRecord
            && userCreationResponse.status &&
            userCreationResponse.status.toLowerCase() === 'succeeded')
            ? t('user_updated_successfully') :
            (!editing && addNewRecord
                && (userCreationResponse && userCreationResponse.status &&
                    userCreationResponse.status.toLowerCase() === 'succeeded')
                || (addAadUserResponse && addAadUserResponse.status && addAadUserResponse.status.toLowerCase() === 'succeeded'))
                ? t('user_created_successfully') : undefined);

    const errorMessage = deleting
        && userDeletionResponse.status
        && userDeletionResponse.status.toLowerCase() !== 'succeeded'
        ? `${t('error_occurred')} ${userDeletionResponse && userDeletionResponse.status}` : (editing || addNewRecord)
            && userCreationResponse.status &&
            userCreationResponse.status.toLowerCase() !== 'succeeded'
            ? `${t('error_occurred')} ${userCreationResponse && userCreationResponse.status}` : undefined;

    function onChangeSearchText(event: any, newValue?: string) {
        setSearchText(newValue);
    }

    function handleOnClick(event: any) {
        let targetValue: number = 0;
        const pageData = event.currentTarget.dataset.arg;
        if (pageData.toLowerCase() === 'previous') {
            targetValue = pageNumber - 1;
        }
        else if (pageData.toLowerCase() === 'next') {
            targetValue = pageNumber + 1;
        }
        else {
            targetValue = parseInt(event.currentTarget.dataset.arg!, 10);
        }
        setPageNumber(targetValue);
        fetchCurrentUsersList(targetValue, '');
    }

    function fetchCurrentUsersList(currPageNum: number, searchData?: string) {
        setUsersList(Array(0));
        while (usersList.length) {
            usersList.pop();
        }
        let filteredUsersList: any = [];
        if (searchText && searchText !== null && searchText !== '') {
            filteredUsersList = getFilteredResults(searchText);
        }
        else {
            filteredUsersList = uniqBy(allUsers, (item: any) => item.retailUserId);
        }
        // Snapshot of the (search-filtered) data before the role/tier filters, used to build the dropdowns.
        const baseUsersList = filteredUsersList;
        if (selectedRole && selectedRole !== null && selectedRole !== '' && selectedRole !== '-1') {
            filteredUsersList = filteredUsersList.filter((item: any) => item['roleName'].toLowerCase() === selectedRole.toLowerCase());
        }
        if (selectedTier && selectedTier !== null && selectedTier !== '' && selectedTier !== '-1') {
            filteredUsersList = filteredUsersList.filter((item: RetailUserType) => String(item.tierName).toLowerCase() === String(selectedTier).toLowerCase());
        }
        setFilteredUsersData(filteredUsersList);
        const startFrom = ((currPageNum - 1) * pageSet);
        const endTo = startFrom + pageSet;
        const totalCounts = filteredUsersList.length;
        const totalPages = (totalCounts % pageSet) > 0 ? parseInt(((totalCounts / pageSet) + 1).toString(), 10) : totalCounts / pageSet;
        setTotalRecordCount(totalCounts);
        setTotalPages(totalPages);

        for (let index = 0; index < filteredUsersList.length; index++) {
            if (index >= startFrom && index < endTo) {
                const element = filteredUsersList[index];
                usersList.push(
                    {
                        channelId: element.channelId,
                        channelName: element.channelName,
                        createdEmailId: element.createdEmailId,
                        domainAlias: element.domainAlias,
                        email: element.email,
                        firstName: element.firstName,
                        isActive: element.isActive,
                        isWarrantyUpdateEnabled: element.isWarrantyUpdateEnabled,
                        lastName: element.lastName,
                        retailUserId: element.retailUserId,
                        roleId: element.roleId,
                        roleName: element.roleName,
                        shiptoCustomerNbr: element.shiptoCustomerNbr,
                        soldToCustomerNbr: element.soldToCustomerNbr,
                        tierName: element.tierName,
                        aadUserId: element.aadUserId,
                        inviteRedeemUrl: element.inviteRedeemUrl,
                        userStatus: element.userStatus,
                        aadUserStatus: element.aadUserStatus,
                        accessRequestSubmittedDate: element.accessRequestSubmittedDate,
                    }
                );
            }
        }
        setUsersList(usersList);

        // Build each dropdown from the data before applying that dropdown's own selected filter,
        // so selecting a role/tier does not remove the other dropdown's options.
        const roleDropdownSource =
            selectedTier && selectedTier !== '-1'
                ? baseUsersList.filter((u: RetailUserType) => String(u.tierName).toLowerCase() === String(selectedTier).toLowerCase())
                : baseUsersList;
        const tierDropdownSource =
            selectedRole && selectedRole !== '-1'
                ? baseUsersList.filter((u: any) => u['roleName'].toLowerCase() === selectedRole.toLowerCase())
                : baseUsersList;
        fetchUserRolesList(roleDropdownSource);
        fetchUserTiersList(tierDropdownSource);
    }

    function onlyUnique(value: any, index: any, self: string | any[]) {
        return self.indexOf(value) === index;
    }

    function fetchUserRolesList(filteredUsersList: any = null) {
        setRolesList(Array(0));
        while (rolesList.length) {
            rolesList.pop();
        }
        const sourceList = filteredUsersList || allUsers;
        if (sourceList && sourceList.length > 0) {
            const filteredRolesList = sourceList.map((item: any) => item.roleName).filter(onlyUnique);
            filteredRolesList.sort();
            setRolesList(filteredRolesList);
        }
    }

    function fetchUserTiersList(filteredUsersList: any = null) {
        setTiersList(Array(0));
        while (tiersList.length) {
            tiersList.pop();
        }
        const sourceList = filteredUsersList || allUsers;
        if (sourceList && sourceList.length > 0) {
            const filteredTiersList = sourceList.map((item: any) => item.tierName).filter(onlyUnique);
            filteredTiersList.sort((a: number, b: number) => {return a - b;});
            setTiersList(filteredTiersList);
        }
    }

    function roleOnChange(event: any) {
        if (event.target && event.target.value) {
            const selectedRoleCode = (event.target.value as unknown) as string;
            setSelectedRole(selectedRoleCode);
        } else {
            setSelectedRole('');
        }
    }

    function tierOnChange(event: any) {
        if (event.target && event.target.value) {
            const selectedTierCode = (event.target.value as unknown) as string;
            setSelectedTier(selectedTierCode);
        } else {
            setSelectedTier('');
        }
    }

    function clearSearch() {
        setSearchText('');
        setSelectedRole('');
        setSelectedTier('');
    }

    function getFormattedColumn(index: number, column: any, element: any) {
        let elementName = '';
        if ((column.fieldName === 'firstName' || column.fieldName === 'lastName'
            || column.fieldName === 'email') &&
            element[column.fieldName].length > 25) {
            elementName = `${element[column.fieldName].substring(0, 25)}...`;
            return <td key={index} role="gridcell" style={column.visible ? { width: column.width } : { display: 'none' }}>
                <div>
                    <TooltipHost
                        aria-label={element[column.fieldName]}
                        content={element[column.fieldName]}>
                        {elementName}
                    </TooltipHost>
                </div>
            </td>;
        }
        return <td key={index} role="gridcell" style={column.visible ? { width: column.width } : { display: 'none' }}>
            <div>
                {element[column.fieldName]}
            </div>
        </td>;
    }

    return (
        <CommonLayout pageTitle={[t('manage_users')]}>
            <section className={Styles.container}>
                <h1 className={Styles.header}>{t('manage_users')}</h1>
                {isLoading ?
                    (<div className={clsx('ms-Grid', Styles.loading)}>
                        <div className="ms-Grid-row">
                            <div className={clsx('ms-Grid-col')}>
                                <BusySpinner
                                    className={Styles.spinner}
                                    messageKey={t('loading')}
                                    size={SpinnerSize.large}
                                />
                            </div>
                        </div>
                    </div>)
                    : (<div className={clsx('ms-Grid', Styles.grid)}>
                        <div className="ms-Grid-row">
                            <div className={clsx('ms-Grid-col')}>
                                <AddOrUpdate
                                    hide={!addNewRecord}
                                    onCloseOrOpen={onCloseOrOpen}
                                    retailUser={retailUser}
                                    userInfo={userInfo}
                                    isEdit={editing}
                                />
                                <Message successMessage={successMessage} errorMessage={errorMessage} />
                            </div>
                        </div>
                        {toolbarButton}
                        <div className="ms-Grid-row" style={{ padding: 15, margin: 0, paddingBottom: 0 }}>
                            <div className={clsx('ms-Grid-col', 'ms-sm12', Styles.toolbarBlock)}>
                                <div className={Styles.dvSearchBlock}>
                                    <Icon iconName="Filter" />
                                    <span className={Styles.dvSearchTextBlock}>
                                        <TextField
                                            ariaLabel={t('searchText')}
                                            id="txt-search-text"
                                            name="searchText"
                                            onChange={onChangeSearchText}
                                            placeholder={t('filter_by_keyword')}
                                            value={searchText} />
                                    </span>
                                    <span className={Styles.dvRolesBlock}>
                                        {rolesList && rolesList.length > 0 &&
                                            (<select onChange={roleOnChange} value={selectedRole} onFocus={onDropdownFocus} onBlur={onDropdownBlur}>
                                                <option key="-1" value="-1">--Role--</option>
                                                {rolesList.map((item: any) => {
                                                    return <option key={item} value={item}>{item}</option>;
                                                })}
                                            </select>)
                                        }
                                    </span>
                                    <span className={Styles.dvTiersBlock}>
                                        {tiersList && tiersList.length > 0 &&
                                            (<select onChange={tierOnChange} value={selectedTier} onFocus={onDropdownFocus} onBlur={onDropdownBlur}>
                                                <option key="-1" value="-1">Tier</option>
                                                {tiersList.map((item: any) => {
                                                    return <option key={item} value={item}>{item}</option>;
                                                })}
                                            </select>)
                                        }
                                    </span>
                                    {((searchText && searchText !== '') || (selectedRole && selectedRole !== '') || (selectedTier && selectedTier !== '')) &&
                                        <span className={Styles.dvClearSearchBlock} onClick={clearSearch}>
                                            <Icon iconName="ClearFilter" />
                                        </span>
                                    }
                                </div>
                            </div>
                        </div>
                        <div className="ms-Grid-row" style={{ padding: 15, margin: 0 }}>
                            <div className={clsx('ms-Grid-col', 'ms-sm12', Styles.gridParent)} style={{ padding: 0 }}>
                                <table className={Styles.tableNonBordered} role="grid">
                                    <thead>
                                        <tr role="row">
                                            {getColumnHeaders(t).map((column: any, index: number) => (
                                                <th key={index} role="gridcell" style={!column.visible ? { display: 'none' } : column.fieldName === 'requestAccess' ? {} : { width: column.width, minWidth: column.width, maxWidth: column.width }}>
                                                    <div>{column.fieldName === 'aadUserStatus' ? column.name : capitalize(column.name)}</div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr role="row" style={users.length > 0 ? { display: 'none' } : { textDecorationColor: 'black' }}>
                                            <td role="gridcell" className={Styles.tdNoRecordFound}>
                                                {t('no_records_found')}
                                            </td>
                                        </tr>
                                        {usersList.map((element: any, index: number) => (
                                            <tr key={index} role="row">
                                                {getColumnHeaders(t).map((column: any, innerIndex: number) => {
                                                    if (column.fieldName === 'actions') {
                                                        return <td key={innerIndex} role="gridcell" style={{ width: column.width, minWidth: column.width, maxWidth: column.width }}>
                                                            {actionButtons(element)}
                                                        </td>;
                                                    }
                                                    if (column.fieldName === 'requestAccess') {
                                                        return <td key={innerIndex} className={Styles.requestAccessCell} role="gridcell">
                                                            <div>{accessStatusMessage(element)}</div>
                                                        </td>;
                                                    }
                                                    if (column.fieldName === 'aadUserStatus') {
                                                        return <td key={innerIndex} role="gridcell" style={{ width: column.width, minWidth: column.width, maxWidth: column.width }}>
                                                            <div>{element.aadUserStatus}</div>
                                                        </td>;
                                                    }
                                                    { return getFormattedColumn(innerIndex, column, element); }
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className={Styles.pagination}>
                                <div>
                                    <ActionButton
                                        ariaLabel={t('FirstPage')}
                                        iconProps={{
                                            ariaLabel: t('FirstPage'),
                                            iconName: 'Previous',
                                        }}
                                        data-arg={'1'}
                                        onClick={handleOnClick}
                                        disabled={pageNumber === 1}
                                    />
                                </div>
                                <div>
                                    <ActionButton
                                        ariaLabel={t('PrevPage')}
                                        iconProps={{
                                            ariaLabel: t('PrevPage'),
                                            iconName: 'CaretSolidLeft',
                                        }}
                                        data-arg={'Previous'}
                                        onClick={handleOnClick}
                                        disabled={pageNumber === 1}
                                    />
                                </div>
                                {renderPageNumbers}
                                <div>
                                    <ActionButton
                                        ariaLabel={t('NextPage')}
                                        iconProps={{
                                            ariaLabel: t('NextPage'),
                                            iconName: 'CaretSolidRight',
                                        }}
                                        data-arg={'Next'}
                                        onClick={handleOnClick}
                                        disabled={totalPages === 1 || pageNumber === totalPages}
                                    />
                                </div>
                                <div>
                                    <ActionButton
                                        ariaLabel={t('LastPage')}
                                        iconProps={{
                                            ariaLabel: t('LastPage'),
                                            iconName: 'Next',
                                        }}
                                        data-arg={totalPages}
                                        onClick={handleOnClick}
                                        disabled={totalPages === 1 || pageNumber === totalPages}
                                    />
                                </div>
                            </div>
                            <div className={Styles.countDisplay}>
                                <div className={Styles.countDisplayText}>
                                    {totalRecordCount === 0 ? 0 : (((pageNumber - 1) * pageSet) + 1)}
                                    &nbsp;-&nbsp;
                                    {totalRecordCount === 0
                                        ? 0
                                        : (totalRecordCount < (((pageNumber - 1) * pageSet) + pageSet)
                                            ? totalRecordCount
                                            : (((pageNumber - 1) * pageSet) + pageSet))}
                                    &nbsp;of&nbsp;{totalRecordCount}
                                </div>
                            </div>
                        </div>
                    </div>)}
            </section>
        </CommonLayout>
    );
}
